import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import _ from 'lodash';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';
import { useFeatures } from 'src/rbac/useFeatures';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useRiskSmartForm } from '../customisable-form/RiskSmartFormContext';
import { useCustomisableFormDataContext } from '../customisable-form-data/CustomisableFormDataContext';
import { ConditionalFieldsContext } from './ConditionalFieldsContext';
import {
  buildFieldConditionGraph,
  getEffectiveFieldConditions,
  getHiddenFields,
  getHiddenSections,
} from './conditionsGraph';

/**
 * Keeps track of which fields are conditionally hidden.
 * Note, needs to be used with a react hook form FormProvider as it needs access to the forms values
 */
export const ConditionalFieldsProvider = ({ children }: PropsWithChildren) => {
  const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set());
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());
  const customisableData = useCustomisableFormDataContext();
  const { formFieldConfigurations } = customisableData;
  // TODO: possibly get parent type by other means to remove dependency on useRiskSmartForm
  const { parentType } = useRiskSmartForm();

  const { watch } = useFormContext();
  const enabledFeatures = useFeatures();
  const currentValues = watch();
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');

  const formConfig = parentType
    ? getFormConfigRegistry(enabledFeatures)[parentType]
    : undefined;

  const fieldConditionGraph = useMemo(
    () =>
      buildFieldConditionGraph(
        getEffectiveFieldConditions(
          formFieldConfigurations,
          formConfig,
          formSectionsEnabled
        )
      ),
    [formFieldConfigurations, formConfig, formSectionsEnabled]
  );

  const helpers = useCustomDatasourceHelpers();

  useEffect(() => {
    if (!parentType) {
      return;
    }

    const hiddenFieldsLatest = getHiddenFields({
      formId: parentType,
      customisableData,
      fieldConditionGraph,
      currentValues,
      helpers,
      enabledFeatures,
      formSectionsEnabled,
    });

    const hiddenSectionsLatest = getHiddenSections({
      formId: parentType,
      customisableData,
      currentValues,
      helpers,
      enabledFeatures,
    });

    setHiddenFields((prev) => {
      if (_.isEqual(prev, hiddenFieldsLatest)) {
        return prev;
      }

      return hiddenFieldsLatest;
    });

    setHiddenSections((prev) => {
      if (_.isEqual(prev, hiddenSectionsLatest)) {
        return prev;
      }

      return hiddenSectionsLatest;
    });
  }, [
    fieldConditionGraph,
    parentType,
    customisableData,
    currentValues,
    helpers,
    enabledFeatures,
    formSectionsEnabled,
  ]);

  return (
    <ConditionalFieldsContext.Provider value={{ hiddenFields, hiddenSections }}>
      {children}
    </ConditionalFieldsContext.Provider>
  );
};
